import React, { useCallback, useEffect, createContext, useState } from 'react';
import { getRoutesFilter } from 'utils/filters';
import * as http from 'utils/http';
import config from 'config';
import serialize from 'utils/serialize';

const PrebookingContext = createContext();

const getPrebookings = async (query) =>
  await http.get(`${config.API_URL}assignment/prebookings/?${query}`);

const getPrebooking = async (prebookingId, query) =>
  await http.get(`${config.API_URL}assignment/prebookings/${prebookingId}?${query}`);

const postCancelPrebooking = async (data) =>
  http.post(`${config.API_URL}cancelacceptedslot/`, data);

const postCompletePrebooking = async ({ id, start_date, end_date }, is_completed) =>
  is_completed
    ? http.post(`${config.API_URL}assignment/prebookings/${id}/complete/`, {
        start_date,
        end_date,
      })
    : http.post(`${config.API_URL}assignment/prebookings/${id}/uncomplete/`, {
        start_date,
        end_date,
      });

function PrebookingContextProvider({ children }) {
  const [mounted, setMounted] = useState(true);
  const [loading, setLoading] = useState(false);

  const [prebookings, setPrebookings] = useState([]);
  const [prebookingsObject, setPrebookingsObject] = useState({});
  const [prebookingFilters, setPrebookingFilters] = useState(null);

  const serializedPrebookingFilters = serialize(prebookingFilters || {});

  const fetchPrebookings = useCallback((ownFilters) => {
    const { routes, date, vehicle_type } = ownFilters || {};

    const prebookingFilters = {
      vehicle_type: (vehicle_type || []).map(({ value }) => value.toLowerCase()).join(','),
      active_at: date,
      page_size: 0,
      ...getRoutesFilter(routes),
    };

    setPrebookingFilters(prebookingFilters);
  }, []);

  const clear = useCallback((ownFilters) => {
    setPrebookingFilters(null);
  }, []);

  const refreshPrebooking = useCallback(
    async (_, prebookingId) => {
      const { active_at } = prebookingFilters || {};

      if (!active_at || !prebookingId) {
        return;
      }

      const prebooking = await getPrebooking(
        prebookingId,
        serialize({
          active_at,
        })
      );

      const index = prebookings.findIndex(({ id }) => prebooking.id === id);

      const newPrebookings = [...prebookings];

      newPrebookings.splice(index, 1, prebooking);

      setPrebookings(newPrebookings);
    },
    [setPrebookings, prebookings, prebookingFilters]
  );

  const _fetchPrebookings = useCallback(
    async function (prebookingFilters) {
      if (!mounted || !prebookingFilters) {
        return;
      }

      setLoading(true);

      try {
        const data = await getPrebookings(prebookingFilters);

        mounted && setPrebookings(data);
      } catch (err) {
        // setError(err);
        console.error(err);
      } finally {
        mounted && setLoading(false);
      }
    },
    [mounted]
  );

  useEffect(() => {
    const prebookingsObject = prebookings.reduce(
      (acc, prebooking) => ({ ...acc, [prebooking.id]: prebooking }),
      {}
    );
    setPrebookingsObject(prebookingsObject);
  }, [prebookings]);

  const cancelPrebooking = useCallback(
    async function (data) {
      try {
        await postCancelPrebooking(data);
      } catch (err) {
        console.error(err);
      } finally {
        _fetchPrebookings(serializedPrebookingFilters);
      }
    },
    [_fetchPrebookings, serializedPrebookingFilters]
  );

  const completePrebooking = useCallback(
    async ({ id, start_date, end_date }, is_completed) => {
      try {
        await postCompletePrebooking({ id, start_date, end_date }, is_completed);
        const prebooking = prebookings.find((p) => p.id === id);
        const prebookingIndex = prebookings.findIndex((p) => p.id === id);
        const newPrebookings = [...prebookings];
        newPrebookings.splice(prebookingIndex, 1, {
          ...prebooking,
          is_completed,
        });
        setPrebookings(newPrebookings);
      } catch (err) {
        console.error(err);
      }
    },
    [prebookings]
  );

  useEffect(() => {
    _fetchPrebookings(serializedPrebookingFilters);
  }, [_fetchPrebookings, serializedPrebookingFilters]);

  useEffect(() => {
    return () => {
      setMounted(false);
    };
  }, []);

  return (
    <PrebookingContext.Provider
      value={{
        fetchPrebookings,
        prebookingsObject,
        prebookings,
        loading,
        cancelPrebooking,
        refreshPrebooking,
        completePrebooking,
        prebookingFilters,
        clear,
      }}
    >
      {children}
    </PrebookingContext.Provider>
  );
}

export { PrebookingContextProvider };
export default PrebookingContext;
